export type AutomationSchedule = {
  daysOfWeek: number[];
  startHour: number;
  endHour: number;
  timezone?: string | null;
};

export const ALLOWED_DAY_VALUES: readonly number[] = [0, 1, 2, 3, 4, 5, 6];

export type DayOfWeekOption = {
  value: number;
  label: string;
  short: string;
};

export const DAYS_OF_WEEK: DayOfWeekOption[] = [
  { value: 0, label: 'Sunday', short: 'S' },
  { value: 1, label: 'Monday', short: 'M' },
  { value: 2, label: 'Tuesday', short: 'T' },
  { value: 3, label: 'Wednesday', short: 'W' },
  { value: 4, label: 'Thursday', short: 'T' },
  { value: 5, label: 'Friday', short: 'F' },
  { value: 6, label: 'Saturday', short: 'S' },
];

const DEFAULT_DAYS = [1, 2, 3, 4, 5];

export const DEFAULT_DAYS_AUTOMATIC_EXECUTIONS = [1] as const;

export const WORKING_HOURS_MIN = 0;
export const WORKING_HOURS_MAX = 24;

export const WORKING_HOUR_OPTIONS = Array.from(
  { length: WORKING_HOURS_MAX - WORKING_HOURS_MIN + 1 },
  (_, index) => WORKING_HOURS_MIN + index,
) as readonly number[];

export type ScheduleHourMark = {
  value: number;
  label: string;
};

export const SCHEDULE_HOUR_MARKS: readonly ScheduleHourMark[] = [
  { value: 0, label: '12 AM' },
  { value: 6, label: '6 AM' },
  { value: 12, label: '12 PM' },
  { value: 18, label: '6 PM' },
  { value: 24, label: 'End' },
];

export const resolveScheduleHour = (
  hour: number | null | undefined,
  fallback: number,
): number => {
  if (hour === null || hour === undefined) {
    return fallback;
  }

  if (!Number.isFinite(hour)) {
    return fallback;
  }

  return hour;
};

export const mergeScheduleHourPartial = ({
  partial,
  current,
}: {
  partial: Partial<Pick<AutomationSchedule, 'startHour' | 'endHour'>>;
  current: Pick<AutomationSchedule, 'startHour' | 'endHour'>;
}): Pick<AutomationSchedule, 'startHour' | 'endHour'> => ({
  startHour:
    partial.startHour !== undefined ? partial.startHour : current.startHour,
  endHour: partial.endHour !== undefined ? partial.endHour : current.endHour,
});

export const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European Time' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (NZST)' },
] as const;

export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

export const formatHourLabel = (hour: number): string => {
  if (hour === 24) return 'End of day';
  if (hour === 0) return '12:00 AM';
  if (hour < 12) return `${hour}:00 AM`;
  if (hour === 12) return '12:00 PM';

  return `${hour - 12}:00 PM`;
};

export const isOvernightHourRange = ({
  startHour,
  endHour,
}: {
  startHour: number;
  endHour: number;
}): boolean => startHour > endHour;

export const formatScheduleTimeRangeLabel = ({
  startHour,
  endHour,
  toLabel,
  nextDayLabel,
  separator = ' ',
}: {
  startHour: number;
  endHour: number;
  toLabel?: string;
  nextDayLabel?: string;
  separator?: string;
}): string => {
  const middle = toLabel ? `${separator}${toLabel}${separator}` : ' – ';
  const range = `${formatHourLabel(startHour)}${middle}${formatHourLabel(endHour)}`;

  if (nextDayLabel && isOvernightHourRange({ startHour, endHour })) {
    return `${range} ${nextDayLabel}`;
  }

  return range;
};

export const normalizeHourRange = ({
  startHour,
  endHour,
}: {
  startHour: number;
  endHour: number;
}): [number, number] => {
  const normalizedStart = Math.min(
    Math.max(startHour, WORKING_HOURS_MIN),
    WORKING_HOURS_MAX - 1,
  );
  const normalizedEnd = Math.min(
    Math.max(endHour, WORKING_HOURS_MIN),
    WORKING_HOURS_MAX,
  );

  if (normalizedEnd === normalizedStart) {
    return [normalizedStart, Math.min(normalizedStart + 1, WORKING_HOURS_MAX)];
  }

  return [normalizedStart, normalizedEnd];
};

export const buildStartHourSelectItems = (
  endHour: number,
): { value: string; label: string }[] =>
  WORKING_HOUR_OPTIONS.filter(
    (hour) => hour < WORKING_HOURS_MAX && hour !== endHour,
  ).map((hour) => ({
    value: String(hour),
    label: formatHourLabel(hour),
  }));

export const buildEndHourSelectItems = (
  startHour: number,
): { value: string; label: string }[] =>
  WORKING_HOUR_OPTIONS.filter((hour) => hour !== startHour).map((hour) => ({
    value: String(hour),
    label: formatHourLabel(hour),
  }));

export const isValidScheduleHourRange = ({
  startHour,
  endHour,
}: {
  startHour: number;
  endHour: number;
}): boolean => startHour !== endHour;

export const normalizeDaysOfWeek = (
  sourceDays: number[] | undefined,
  defaultDays: readonly number[] = DEFAULT_DAYS,
): number[] => {
  const base = sourceDays ?? [...defaultDays];
  const normalizedDays = Array.from(
    new Set(base.filter((day) => ALLOWED_DAY_VALUES.includes(day))),
  ).sort((leftDay, rightDay) => leftDay - rightDay);

  return normalizedDays.length > 0 ? normalizedDays : [...defaultDays];
};

export const resolveScheduleTimezoneSelection = ({
  scheduleTimezone,
  browserTimezone,
}: {
  scheduleTimezone: string | null | undefined;
  browserTimezone: string;
}): string => {
  const timezoneValue = scheduleTimezone?.trim();

  if (timezoneValue && timezoneValue.length > 0) {
    return timezoneValue;
  }

  return browserTimezone;
};

export type TimezoneSelectItem = {
  value: string;
  label: string;
};

export const buildTimezoneSelectItems = ({
  browserTimezone,
  selectedTimezone,
}: {
  browserTimezone: string;
  selectedTimezone: string;
}): TimezoneSelectItem[] => {
  const items: TimezoneSelectItem[] = TIMEZONES.map((timezoneOption) => ({
    value: timezoneOption.value,
    label:
      timezoneOption.value === browserTimezone
        ? `${timezoneOption.label} (Your timezone)`
        : timezoneOption.label,
  }));

  if (
    !TIMEZONES.some(
      (timezoneOption) => timezoneOption.value === browserTimezone,
    )
  ) {
    items.unshift({
      value: browserTimezone,
      label: `${browserTimezone} (Your timezone)`,
    });
  }

  if (
    !items.some((timezoneOption) => timezoneOption.value === selectedTimezone)
  ) {
    items.unshift({
      value: selectedTimezone,
      label:
        selectedTimezone === browserTimezone
          ? `${selectedTimezone} (Your timezone)`
          : selectedTimezone,
    });
  }

  return items;
};
